import React, { useMemo, useState } from "react";
import useReadingsData from "../hooks/useReadingsData";
import SummaryMetricCard from "../../../shared/ui/SummaryMetricCard";

const sanitizeDecimalInput = (raw) => {
  const s = String(raw ?? "");
  let clean = s.replace(/[^0-9.,]/g, "");

  const firstSepIdx = clean.search(/[.,]/);
  if (firstSepIdx !== -1) {
    const before = clean.slice(0, firstSepIdx + 1);
    const after = clean.slice(firstSepIdx + 1).replace(/[.,]/g, "");
    clean = before + after;
  }

  return clean;
};

const formatDateBR = (value) => {
  if (!value) return "Sem data informada";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sem data informada";

  return date.toLocaleDateString("pt-BR");
};

const formatNumber = (value, digits = 1) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";

  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const resolveRecordDate = (item) => {
  if (!item) return null;
  return item.date ?? item.createdAt ?? item.loggedAt ?? item.measuredAt ?? null;
};

const resolveMeasureValue = (item, key) => {
  if (!item) return null;

  return (
    item[key] ??
    item[key.replace("Cm", "_cm")] ??
    item[key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`)] ??
    null
  );
};

function IconBadge({ children }) {
  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#ECE7FA] text-[#6C54D8]">
      {children}
    </div>
  );
}

function ScaleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 4v14"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M7 7h10"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M9 7 6.5 12h5L9 7Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M15 7 12.5 12h5L15 7Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M8 20h8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MeasureIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 17.5 17.5 4a1.4 1.4 0 0 1 2 0l.5.5a1.4 1.4 0 0 1 0 2L6.5 20a1.4 1.4 0 0 1-2 0l-.5-.5a1.4 1.4 0 0 1 0-2Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="m13 8 3 3" stroke="currentColor" strokeWidth="1.8" />
      <path d="M10.5 10.5 9 12" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 13 6.5 14.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function PressureIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 20s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 10c0 5.65-7 10-7 10Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M9 12h1.5l1-2 1 4 1-2H15"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GlucoseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3c4.5 0 8 2.8 8 6.5 0 5.7-8 11.5-8 11.5S4 15.2 4 9.5C4 5.8 7.5 3 12 3Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M12 7v5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <circle cx="12" cy="15.5" r="1" fill="currentColor" />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 4v5h5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 12a8 8 0 1 1-2.34-5.66L20 9"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 8v4l2.5 1.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      className={`transition-transform ${open ? "rotate-180" : ""}`}
      aria-hidden="true"
    >
      <path
        d="m6 9 6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EmptyState({ text }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#ECE7FA] bg-[#FAFAFC] p-3 text-xs font-semibold text-[#6D6680] text-center">
      {text}
    </div>
  );
}

function SummaryQuickCard({ areasCount, pendingCount }) {
  return (
    <section className="liti-card p-4 shadow-sm border border-[#ECE7FA]">
      <div className="text-xs font-bold uppercase tracking-wider text-[#6D6680]">
        Resumo rápido
      </div>

      <div className="mt-2.5 grid grid-cols-2 gap-2.5">
        <SummaryMetricCard
          title="Registros"
          value={`${areasCount} áreas`}
          size="compact"
          variant="soft"
          softSurface
        />
        <SummaryMetricCard
          title="Hoje"
          value={`${pendingCount} pendência(s)`}
          size="compact"
          variant="soft"
          softSurface
        />
      </div>
    </section>
  );
}

function SoftMetricBox({ label, value, align = "left" }) {
  return (
    <div
      className={`rounded-2xl border border-[#ECE7FA] bg-[#FAFAFC] px-3 py-2 ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#6D6680]">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-bold text-[#24152F]">{value}</div>
    </div>
  );
}

function HistoryToggle({ open, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between rounded-2xl border border-[#ECE7FA] bg-[#FAFAFC] px-3.5 py-2.5 text-left transition hover:bg-white cursor-pointer"
    >
      <div>
        <div className="text-xs font-bold text-[#24152F]">Histórico</div>
        <div className="mt-0.5 text-[11px] font-semibold text-[#6D6680]">
          {open ? "Ocultar registros" : "Expandir registros"}
        </div>
      </div>

      <div className="text-[#6C54D8]">
        <ChevronIcon open={open} />
      </div>
    </button>
  );
}

function WeightHistoryItem({ item, onEdit }) {
  const value = item?.weightKg ?? item?.weight_kg;
  const date = resolveRecordDate(item);

  return (
    <div className="rounded-2xl border border-[#ECE7FA] bg-white px-3.5 py-2.5">
      <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3">
        <div className="text-xs font-semibold text-[#24152F]">
          {formatDateBR(date)}
        </div>

        <div className="text-xs font-black text-[#6C54D8] justify-self-center font-serif">
          {formatNumber(value, 1)} kg
        </div>

        <button
          type="button"
          onClick={onEdit}
          className="btn-secondary h-8 px-3 text-xs font-bold shrink-0"
        >
          Editar
        </button>
      </div>

      {item?.notes ? (
        <div className="mt-1 text-xs text-[#6D6680]">{item.notes}</div>
      ) : null}
    </div>
  );
}

function BodyPairBox({ label, leftLabel, leftValue, rightLabel, rightValue }) {
  return (
    <div className="rounded-2xl border border-[#ECE7FA] bg-[#FAFAFC] px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#6D6680]">
        {label}
      </div>
      <div className="mt-1 grid grid-cols-2 gap-2">
        <div>
          <div className="text-[11px] font-semibold text-[#6D6680]">{leftLabel}</div>
          <div className="mt-0.5 text-xs font-bold text-[#24152F]">
            {leftValue}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-semibold text-[#6D6680]">{rightLabel}</div>
          <div className="mt-0.5 text-xs font-bold text-[#24152F]">
            {rightValue}
          </div>
        </div>
      </div>
    </div>
  );
}

function BodyDetailsGrid({ item, bodyFieldGroups }) {
  return (
    <div className="space-y-2">
      {bodyFieldGroups.map((group) => {
        const filledFields = group.fields
          .map((field) => ({
            ...field,
            value: resolveMeasureValue(item, field.key),
          }))
          .filter(
            (field) =>
              field.value !== null &&
              field.value !== undefined &&
              field.value !== ""
          );

        if (!filledFields.length) return null;

        return (
          <div
            key={group.id}
            className={`grid gap-2 ${
              group.columns === 2 ? "grid-cols-2" : "grid-cols-1"
            }`}
          >
            {filledFields.map((field) => (
              <div
                key={field.key}
                className="rounded-2xl border border-[#ECE7FA] bg-[#FAFAFC] px-3 py-2"
              >
                <div className="text-xs text-[#6D6680] font-medium">{field.label}</div>
                <div className="mt-0.5 text-xs font-bold text-[#24152F]">
                  {formatNumber(field.value, 1)} cm
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function BodyHistoryItem({ item, bodyFieldGroups, compactSummary, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const date = resolveRecordDate(item);

  return (
    <div className="rounded-2xl border border-[#ECE7FA] bg-white px-3.5 py-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-bold text-[#24152F]">{formatDateBR(date)}</div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="btn-secondary h-8 px-3 text-xs font-bold"
          >
            Editar
          </button>

          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="btn-secondary h-8 px-3 text-xs font-bold"
          >
            {expanded ? "Ocultar" : "Ver detalhes"}
          </button>
        </div>
      </div>

      {!expanded ? (
        <div className="text-xs leading-relaxed text-[#6D6680]">{compactSummary}</div>
      ) : (
        <div className="pt-1">
          <BodyDetailsGrid item={item} bodyFieldGroups={bodyFieldGroups} />
        </div>
      )}

      {item?.notes ? (
        <div className="text-xs text-[#6D6680]">{item.notes}</div>
      ) : null}
    </div>
  );
}

function ReadingHeroCard({ icon, title, actionIcon, children }) {
  return (
    <section className="liti-card p-4 shadow-sm border border-[#ECE7FA]">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <IconBadge>{icon}</IconBadge>
          <h2 className="text-base font-bold text-[#24152F] font-serif">{title}</h2>
        </div>

        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#ECE7FA] text-[#6C54D8]">
          {actionIcon}
        </div>
      </div>

      <div className="mt-3.5">{children}</div>
    </section>
  );
}

function WeightRegisterBlock({
  weightDate,
  setWeightDate,
  weightKg,
  setWeightKg,
  weightNotes,
  setWeightNotes,
  handleSaveWeight,
  onClose,
  isEditing,
}) {
  return (
    <div className="mt-3 space-y-3 rounded-2xl border border-[#ECE7FA] bg-[#FAFAFC] p-3.5">
      <div className="grid gap-2.5 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#6D6680]">Data</span>
          <input
            type="date"
            value={weightDate}
            onChange={(e) => setWeightDate(e.target.value)}
            className="input-base w-full h-11 text-xs font-medium"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#6D6680]">
            Peso atual (kg)
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={weightKg}
            onChange={(e) => setWeightKg(sanitizeDecimalInput(e.target.value))}
            placeholder="Ex.: 78,4"
            className="input-base w-full h-11 text-xs font-medium"
          />
        </label>
      </div>

      <label className="block">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#6D6680]">
          Observações
        </span>
        <textarea
          rows={3}
          value={weightNotes}
          onChange={(e) => setWeightNotes(e.target.value)}
          placeholder="Opcional"
          className="input-base w-full h-auto py-2.5 text-xs font-medium"
        />
      </label>

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="btn-secondary h-10 px-4 text-xs font-bold"
        >
          Cancelar
        </button>

        <button
          type="button"
          onClick={handleSaveWeight}
          className="btn-primary h-10 px-5 text-xs font-bold"
        >
          {isEditing ? "Atualizar peso" : "Salvar peso"}
        </button>
      </div>
    </div>
  );
}

function BodyRegisterBlock({
  bodyDate,
  setBodyDate,
  bodyNotes,
  setBodyNotes,
  body,
  setBody,
  bodyFieldGroups,
  handleSaveBody,
  onClose,
  isEditing,
}) {
  return (
    <div className="mt-3 space-y-3 rounded-2xl border border-[#ECE7FA] bg-[#FAFAFC] p-3.5">
      <div className="grid gap-2.5 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#6D6680]">Data</span>
          <input
            type="date"
            value={bodyDate}
            onChange={(e) => setBodyDate(e.target.value)}
            className="input-base w-full h-11 text-xs font-medium"
          />
        </label>

        <div className="rounded-2xl border border-[#ECE7FA] bg-white p-3 text-xs leading-relaxed text-[#6D6680] font-medium flex items-center">
          Preencha apenas as medidas que tiver hoje.
        </div>
      </div>

      <div className="space-y-2.5">
        {bodyFieldGroups.map((group) => (
          <div
            key={group.id}
            className={`grid gap-2.5 ${
              group.columns === 2 ? "grid-cols-2" : "grid-cols-1"
            }`}
          >
            {group.fields.map((field) => (
              <label key={field.key} className="block">
                <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-[#6D6680]">
                  {field.label} (cm)
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={body[field.key] ?? ""}
                  onChange={(e) => {
                    const next = sanitizeDecimalInput(e.target.value);
                    setBody((prev) => ({
                      ...prev,
                      [field.key]: next,
                    }));
                  }}
                  placeholder={field.placeholder}
                  className="input-base w-full h-10 text-xs font-medium"
                />
              </label>
            ))}
          </div>
        ))}
      </div>

      <label className="block">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#6D6680]">
          Observações
        </span>
        <textarea
          rows={3}
          value={bodyNotes}
          onChange={(e) => setBodyNotes(e.target.value)}
          placeholder="Opcional"
          className="input-base w-full h-auto py-2.5 text-xs font-medium"
        />
      </label>

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="btn-secondary h-10 px-4 text-xs font-bold"
        >
          Cancelar
        </button>

        <button
          type="button"
          onClick={handleSaveBody}
          className="btn-primary h-10 px-5 text-xs font-bold"
        >
          {isEditing ? "Atualizar medidas" : "Salvar medidas"}
        </button>
      </div>
    </div>
  );
}

function PlaceholderHealthCard({ icon, title, description }) {
  return (
    <section className="liti-card p-4 shadow-sm border border-[#ECE7FA]">
      <div className="flex items-center gap-3">
        <IconBadge>{icon}</IconBadge>
        <h2 className="text-base font-bold text-[#24152F] font-serif">{title}</h2>
      </div>

      <div className="mt-3 rounded-2xl border border-[#ECE7FA] bg-[#FAFAFC] p-3 text-xs leading-relaxed text-[#6D6680] font-medium">
        {description}
      </div>
    </section>
  );
}

function buildPairedMeasureBoxes(item, profileGender) {
  if (!item) return [];

  const isFemale = String(profileGender).toLowerCase().includes("femin");
  const chestLabel = isFemale ? "Busto" : "Peito";

  return [
    {
      label: "Braço",
      leftLabel: "Esquerdo",
      leftValue: `${formatNumber(resolveMeasureValue(item, "leftArmCm"), 1)} cm`,
      rightLabel: "Direito",
      rightValue: `${formatNumber(resolveMeasureValue(item, "rightArmCm"), 1)} cm`,
    },
    {
      label: "Panturrilha",
      leftLabel: "Esquerda",
      leftValue: `${formatNumber(resolveMeasureValue(item, "leftCalfCm"), 1)} cm`,
      rightLabel: "Direita",
      rightValue: `${formatNumber(resolveMeasureValue(item, "rightCalfCm"), 1)} cm`,
    },
    {
      label: "Cintura e quadril",
      leftLabel: "Cintura",
      leftValue: `${formatNumber(resolveMeasureValue(item, "waistCm"), 1)} cm`,
      rightLabel: "Quadril",
      rightValue: `${formatNumber(resolveMeasureValue(item, "hipCm"), 1)} cm`,
    },
    {
      label: chestLabel,
      leftLabel: chestLabel,
      leftValue: `${formatNumber(resolveMeasureValue(item, "chestCm"), 1)} cm`,
      rightLabel: "Registros",
      rightValue: "último",
    },
  ];
}

export default function ReadingsContent() {
  const {
    loading,
    message,

    profileGender,

    weightDate,
    setWeightDate,
    weightKg,
    setWeightKg,
    weightNotes,
    setWeightNotes,
    latestWeight,
    weightHistory,
    editingWeightId,
    startEditWeight,
    cancelEditWeight,

    showBodyForm,
    setShowBodyForm,
    bodyDate,
    setBodyDate,
    bodyNotes,
    setBodyNotes,
    latestBody,
    bodyHistory,
    body,
    setBody,
    editingBodyId,
    startEditBody,
    cancelEditBody,

    handleSaveWeight,
    handleSaveBody,
  } = useReadingsData();

  const [showWeightForm, setShowWeightForm] = useState(false);
  const [showWeightHistory, setShowWeightHistory] = useState(false);
  const [showBodyHistory, setShowBodyHistory] = useState(false);

  const isFemale = useMemo(
    () => String(profileGender).toLowerCase().includes("femin"),
    [profileGender]
  );

  const isMale = useMemo(
    () => String(profileGender).toLowerCase().includes("mascul"),
    [profileGender]
  );

  const bodyFieldGroups = useMemo(() => {
    const chestLabel = isFemale
      ? "Busto / tórax"
      : isMale
      ? "Peitoral / tórax"
      : "Tórax";

    return [
      {
        id: "arms",
        columns: 2,
        fields: [
          { key: "leftArmCm", label: "Braço esquerdo", placeholder: "Ex.: 32,5" },
          { key: "rightArmCm", label: "Braço direito", placeholder: "Ex.: 32,5" },
        ],
      },
      {
        id: "center",
        columns: 2,
        fields: [
          { key: "waistCm", label: "Cintura", placeholder: "Ex.: 84" },
          { key: "hipCm", label: "Quadril", placeholder: "Ex.: 102" },
        ],
      },
      {
        id: "chest",
        columns: 1,
        fields: [
          {
            key: "chestCm",
            label: chestLabel,
            placeholder: isMale ? "Ex.: 102" : "Ex.: 96",
          },
        ],
      },
      {
        id: "thighs",
        columns: 2,
        fields: [
          { key: "leftThighCm", label: "Coxa esquerda", placeholder: "Ex.: 58" },
          { key: "rightThighCm", label: "Coxa direita", placeholder: "Ex.: 58" },
        ],
      },
      {
        id: "calves",
        columns: 2,
        fields: [
          {
            key: "leftCalfCm",
            label: "Panturrilha esquerda",
            placeholder: "Ex.: 37",
          },
          {
            key: "rightCalfCm",
            label: "Panturrilha direita",
            placeholder: "Ex.: 37",
          },
        ],
      },
    ];
  }, [isFemale, isMale]);

  const latestWeightDisplay =
    latestWeight?.weightKg ?? latestWeight?.weight_kg ?? null;
  const latestWeightDate = resolveRecordDate(latestWeight);
  const previousWeight =
    weightHistory?.[1]?.weightKg ?? weightHistory?.[1]?.weight_kg ?? null;

  const weightDiff = useMemo(() => {
    if (
      latestWeightDisplay === null ||
      latestWeightDisplay === undefined ||
      previousWeight === null ||
      previousWeight === undefined
    ) {
      return null;
    }

    const diff = Number(latestWeightDisplay) - Number(previousWeight);
    if (!Number.isFinite(diff)) return null;
    return diff;
  }, [latestWeightDisplay, previousWeight]);

  const weightTrend = useMemo(() => {
    if (weightDiff === null) return "sem histórico";
    if (weightDiff < 0) return "caindo";
    if (weightDiff > 0) return "subindo";
    return "estável";
  }, [weightDiff]);

  const latestBodyDate = resolveRecordDate(latestBody);

  const buildHistoryBodyCompactSummary = (item) => {
    if (!item) return "Sem medidas visíveis neste registro.";

    const boxes = buildPairedMeasureBoxes(item, profileGender);

    return boxes
      .map((box) => `${box.label}: ${box.leftValue} • ${box.rightValue}`)
      .join(" • ");
  };

  const pairedLatestMeasures = useMemo(
    () => buildPairedMeasureBoxes(latestBody, profileGender),
    [latestBody, profileGender]
  );

  const areasCount = 4;
  const pendingCount = [!latestWeight, !latestBody, true, true].filter(Boolean).length;

  const openWeightEdit = (item) => {
    startEditWeight(item);
    setShowWeightForm(true);
  };

  const closeWeightForm = () => {
    cancelEditWeight();
    setShowWeightForm(false);
  };

  const openBodyEdit = (item) => {
    startEditBody(item);
    setShowBodyHistory(true);
  };

  const closeBodyForm = () => {
    cancelEditBody();
    setShowBodyForm(false);
  };

  if (loading) {
    return (
      <section className="mx-auto w-full space-y-4">
        <div className="liti-card p-4 text-center text-xs font-bold text-[#6D6680]">
          Carregando registros...
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto w-full space-y-4">
      {message?.text ? (
        <div
          className={`rounded-2xl px-4 py-3 text-xs font-semibold shadow-xs ${
            message.type === "success"
              ? "bg-[#CBF1DB] text-emerald-900"
              : "bg-red-50 text-red-700"
          }`}
        >
          {message.text}
        </div>
      ) : null}

      <SummaryQuickCard areasCount={areasCount} pendingCount={pendingCount} />

      <div className="grid gap-4 xl:grid-cols-2">
        <ReadingHeroCard
          icon={<ScaleIcon />}
          title="Peso"
          actionIcon={<HistoryIcon />}
        >
          <div className="mx-auto w-full rounded-2xl border border-[#ECE7FA] bg-[#FAFAFC] p-3 space-y-3">
            <div className="grid grid-cols-2 items-start gap-2">
              <div>
                <div className="text-xs font-semibold text-[#6D6680]">
                  {formatDateBR(latestWeightDate)}
                </div>
                <div className="mt-1 text-2xl font-bold leading-none text-[#24152F] font-serif">
                  {latestWeightDisplay !== null && latestWeightDisplay !== undefined
                    ? `${formatNumber(latestWeightDisplay, 1)} kg`
                    : "—"}
                </div>
              </div>

              <SoftMetricBox
                label="Tendência"
                value={weightTrend}
                align="right"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <SoftMetricBox
                label="Diferença"
                value={
                  weightDiff === null
                    ? "—"
                    : `${weightDiff > 0 ? "+" : ""}${formatNumber(weightDiff, 1)} kg`
                }
              />
              <SoftMetricBox
                label="Registros"
                value={`${weightHistory?.length || 0} item(ns)`}
                align="right"
              />
            </div>

            <div className="pt-1">
              <button
                type="button"
                onClick={() => {
                  if (editingWeightId) {
                    closeWeightForm();
                  } else {
                    setShowWeightForm((prev) => !prev);
                  }
                }}
                className="btn-primary w-full h-11 text-xs font-bold uppercase tracking-wider shadow-sm flex items-center justify-center gap-2"
              >
                <span className="text-base leading-none">
                  {editingWeightId ? "×" : "+"}
                </span>
                <span>
                  {editingWeightId
                    ? "Cancelar edição de peso"
                    : "Registrar peso"}
                </span>
              </button>
            </div>

            {showWeightForm ? (
              <WeightRegisterBlock
                weightDate={weightDate}
                setWeightDate={setWeightDate}
                weightKg={weightKg}
                setWeightKg={setWeightKg}
                weightNotes={weightNotes}
                setWeightNotes={setWeightNotes}
                handleSaveWeight={handleSaveWeight}
                onClose={closeWeightForm}
                isEditing={Boolean(editingWeightId)}
              />
            ) : null}
          </div>

          <div className="mt-2.5">
            <HistoryToggle
              open={showWeightHistory}
              onClick={() => setShowWeightHistory((prev) => !prev)}
            />
          </div>

          {showWeightHistory ? (
            <div className="mt-2.5 space-y-2">
              {weightHistory?.length ? (
                weightHistory.map((item, index) => (
                  <WeightHistoryItem
                    key={`${resolveRecordDate(item) || "weight"}-${index}`}
                    item={item}
                    onEdit={() => openWeightEdit(item)}
                  />
                ))
              ) : (
                <EmptyState text="Seu histórico de peso vai aparecer aqui." />
              )}
            </div>
          ) : null}
        </ReadingHeroCard>

        <ReadingHeroCard
          icon={<MeasureIcon />}
          title="Medidas corporais"
          actionIcon={<HistoryIcon />}
        >
          <div className="mx-auto w-full rounded-2xl border border-[#ECE7FA] bg-[#FAFAFC] p-3 space-y-3">
            <div className="grid grid-cols-[1.22fr_0.78fr] items-start gap-2">
              <div>
                <div className="text-xs font-semibold text-[#6D6680]">Último registro</div>
                <div className="mt-1 text-base font-bold text-[#24152F] font-serif">
                  {formatDateBR(latestBodyDate)}
                </div>
              </div>

              <SoftMetricBox
                label="Registros"
                value={`${bodyHistory?.length || 0} item(ns)`}
                align="right"
              />
            </div>

            <div className="space-y-2">
              {latestBody ? (
                pairedLatestMeasures.map((box) => (
                  <BodyPairBox
                    key={box.label}
                    label={box.label}
                    leftLabel={box.leftLabel}
                    leftValue={box.leftValue}
                    rightLabel={box.rightLabel}
                    rightValue={box.rightValue}
                  />
                ))
              ) : (
                <EmptyState text="Você ainda não registrou medidas." />
              )}
            </div>

            <div className="pt-1">
              <button
                type="button"
                onClick={() => {
                  if (editingBodyId) {
                    closeBodyForm();
                  } else {
                    setShowBodyForm((prev) => !prev);
                  }
                }}
                className="btn-primary w-full h-11 text-xs font-bold uppercase tracking-wider shadow-sm flex items-center justify-center gap-2"
              >
                <span className="text-base leading-none">
                  {editingBodyId ? "×" : "+"}
                </span>
                <span>
                  {editingBodyId
                    ? "Cancelar edição de medidas"
                    : "Registrar medidas"}
                </span>
              </button>
            </div>

            {showBodyForm ? (
              <BodyRegisterBlock
                bodyDate={bodyDate}
                setBodyDate={setBodyDate}
                bodyNotes={bodyNotes}
                setBodyNotes={setBodyNotes}
                body={body}
                setBody={setBody}
                bodyFieldGroups={bodyFieldGroups}
                handleSaveBody={handleSaveBody}
                onClose={closeBodyForm}
                isEditing={Boolean(editingBodyId)}
              />
            ) : null}
          </div>

          <div className="mt-2.5">
            <HistoryToggle
              open={showBodyHistory}
              onClick={() => setShowBodyHistory((prev) => !prev)}
            />
          </div>

          {showBodyHistory ? (
            <div className="mt-2.5 space-y-2">
              {bodyHistory?.length ? (
                bodyHistory.map((item, index) => (
                  <BodyHistoryItem
                    key={`${resolveRecordDate(item) || "body"}-${index}`}
                    item={item}
                    bodyFieldGroups={bodyFieldGroups}
                    compactSummary={buildHistoryBodyCompactSummary(item)}
                    onEdit={() => openBodyEdit(item)}
                  />
                ))
              ) : (
                <EmptyState text="Seu histórico de medidas vai aparecer aqui." />
              )}
            </div>
          ) : null}
        </ReadingHeroCard>

        <PlaceholderHealthCard
          icon={<PressureIcon />}
          title="Pressão arterial"
          description="Em breve você poderá registrar sua pressão arterial, ver o último registro e consultar o histórico de aferições."
        />

        <PlaceholderHealthCard
          icon={<GlucoseIcon />}
          title="Glicemia"
          description="Em breve você poderá registrar sua glicemia, ver o último registro e consultar o histórico."
        />
      </div>
    </section>
  );
}